import { formatPhone } from '@/utils'
import type { ProcedureReferralFormState } from '../procedureReferralFormData'
import {
  distinctPracticeName,
  recipientName,
  type SelectedRecipient,
} from '../referral/referralTypes'
import type { OrderPdfData, OrderPdfSection, AutoPopulateData } from './pdfTypes'
import { referralClosingDetail, referralLetterDate } from './pdfHelpers'

/** "Name — Specialty", or just the name when there's no specialty label to show. */
function recipientDisplayName(
  recipient: SelectedRecipient,
  specialtyLabelOverride: string,
): string {
  const specialtyLabel = specialtyLabelOverride || recipient.specialtyLabel
  return specialtyLabel
    ? `${recipientName(recipient)} — ${specialtyLabel}`
    : recipientName(recipient)
}

export function mapProcedureReferralToPdf(
  form: ProcedureReferralFormState,
  auto: AutoPopulateData,
  recipient: SelectedRecipient | undefined,
  now: Date = new Date(),
): OrderPdfData {
  const icd10Codes = form.diagnosisText
    ? form.diagnosisText
        .split(/[;\n]/)
        .map((s) => s.trim())
        .filter(Boolean)
    : []

  const vendorSections: OrderPdfSection[] = []

  const referringToName = recipient ? recipientDisplayName(recipient, form.specialtyLabel) : ''

  if (recipient) {
    vendorSections.push({
      title: 'Referral Details',
      type: 'keyValue',
      data: {
        'Referral Type': form.isEvaluationOnly ? 'Consultation / Evaluation' : 'Procedure Referral',
        'Referring To': referringToName,
        ...(distinctPracticeName(recipient) ? { Practice: distinctPracticeName(recipient) } : {}),
        'Specialist NPI': recipient.npi || 'N/A',
        'Specialist Phone': recipient.phone || 'N/A',
        'Specialist Fax': formatPhone(recipient.fax) || 'N/A',
      },
    })
  }

  const summary = form.summary.trim()
  if (summary) {
    vendorSections.push({
      title: 'Clinical Summary',
      type: 'text',
      content: form.summary,
    })
  }
  const hasLetter = Boolean(form.indication.trim())

  const facilityHeader = recipient
    ? {
        name: referringToName,
        address: `${recipient.street}, ${recipient.city}, ${recipient.state} ${recipient.zip}`,
        fax: formatPhone(recipient.fax) || 'N/A',
      }
    : undefined

  const referralCategory = form.isEvaluationOnly
    ? 'Consultation'
    : form.specialtyLabel || 'Referral'

  return {
    vendorName: 'Procedure Referral',
    orderTitle: form.isEvaluationOnly
      ? 'Specialist Consultation Referral'
      : 'Procedure / Referral Order',
    facilityHeader,
    patient: {
      firstName: auto.patient.firstName,
      middleName: auto.patient.middleName,
      lastName: auto.patient.lastName,
      dateOfBirth: auto.patient.dateOfBirth,
      sex: auto.patient.sexCode,
      email: auto.patient.email,
      phone: auto.patient.phone,
      address: auto.patient.address,
      city: auto.patient.city,
      state: auto.patient.state,
      zip: auto.patient.zip,
      country: auto.patient.country,
      patientId: auto.patient.patientId,
    },
    provider: {
      organizationName: auto.provider.organizationName,
      providerName: auto.provider.providerName,
      npi: auto.provider.npi,
      phone: auto.provider.orgPhone,
      fax: auto.provider.orgFax,
      address: auto.provider.orgAddress,
      city: auto.provider.orgCity,
      state: auto.provider.orgState,
      zip: auto.provider.orgZip,
      email: auto.provider.providerEmail,
    },
    insurance: {
      primaryCarrier: auto.insurance.primaryCarrier,
      primaryMemberId: auto.insurance.primaryMemberId,
      primaryGroup: auto.insurance.primaryGroup,
      policyholderName: auto.insurance.policyholderName,
      relationship: auto.insurance.relationship,
    },
    icd10Codes,
    selectedTests: form.selectedCptCodes.map((cpt) => ({
      code: cpt.code,
      name: cpt.display,
      category: referralCategory,
    })),
    vendorSections,
    coverSheet: recipient
      ? {
          to: recipientName(recipient),
          toPractice: distinctPracticeName(recipient),
          toFax: formatPhone(recipient.fax),
          fromName: auto.provider.providerName,
          fromOrganization: auto.provider.organizationName,
          fromPhone: auto.provider.orgPhone,
          fromFax: auto.provider.orgFax,
          date: referralLetterDate(now),
          patientName: [auto.patient.firstName, auto.patient.lastName].filter(Boolean).join(' '),
          patientDob: auto.patient.dateOfBirth,
          enclosed: [
            ...(hasLetter ? ['Referral letter'] : []),
            ...(summary ? ['clinical summary'] : []),
            'referral requisition',
          ],
        }
      : undefined,
    letter: hasLetter
      ? {
          date: referralLetterDate(now),
          body: form.indication,
          closingName: auto.provider.providerName,
          closingDetail: referralClosingDetail(auto.provider),
        }
      : undefined,
    signatureDataUrl: form.providerSignatureDataUrl,
    signatureDate: form.providerSignatureDate,
  }
}
